import { useState, useEffect, useMemo, useCallback } from "react";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import { ColumnSort } from "@tanstack/react-table";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useParams as useParamsHook, useSearchWithParams } from "@/hooks/useSearchParams";
import DateSelect from "@/components/DateSelect";
import SearchInput from "@/components/SearchInput";
import { Table as TableComponent } from "@tanstack/react-table";
import { StudentsQueryVariables } from "./types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { STUDENTS_QUERY } from "./queries";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import Button from "@/components/Button";
import usePagination from "@/hooks/usePagination";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import useModal from "@/hooks/useModal";
import Banner from "@/components/Banner";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { buildPATStatusForNonMembers } from "@/features/person/utils/utils";
import { buildColumns, mapStudentsSimplified } from "./_utils";
import { StudentRow, PatStatus, UniversityStudentsQuery } from "./types";
import { CREATE_GRADUATION_REQUESTS_MUTATION } from "./mutations";
import ProbationModal from "./probations/ProbationModal";
import EditCohortDialog from "./EditCohortDialog";
import pluralize from "@/lib/pluralize";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import MultiSelect from "@/components/MultiSelect";
import {
  GET_COHORT_ENROLLMENT_NOTES_BY_UNIVERSITY_QUERY,
  type GetCohortEnrollmentNotesByUniversityQuery
} from "./[studentId]/notes/queries";
import { NoteNotableValues } from "@/types";
import useCapabilities from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { UNIVERSITY_QUERY } from "../queries";
import { PENDING_REVIEW_ROSTER_STATUS, UNIVERSITY_ROSTER_ROWS_EXISTS_QUERY } from "./queries";
import {
  CampusPendingRosterBanner,
  CampusPendingRosterCheckFailedBanner,
  campusPendingRosterGate
} from "./_components/CampusPendingRosterBanner";
import { GET_ACTIVE_UNIVERSITY_PROBATIONS_QUERY } from "./probations/queries";
import { buildStudentProbationsMap } from "./probations/_utils";
import { ExpandedProgramLevelsRow } from "../../_shared/_components/ExpandedProgramLevelsRow";
import NotesTable from "@/features/global/components/NotesTable";
import {
  getStudentNotes,
  getStudentEnrollmentId,
  type ExpansionState,
  type ExpandedColumn
} from "../../_shared/_utils";
import { FILTER_PROGRAM_LEVEL_OPTIONS, PAT_STATUS_OPTIONS, mapPATStatusesToGraphQL } from "../../_shared/constants";
import { parseProgramLevelFilterSelections } from "../../_shared/programLevelFilter";
import { PATStatusExportRow } from "./types";
import { MaterialNames } from "@/components/material_symbol_names";
import { generateAllStudentsPATStatusPDF } from "./_utils/patStatusPdfGenerator";

export default function UniversityStudentsPage() {
  const { shouldShowPersonProfileActions, canAccessPeoplePages, isCampusOnlyUniversityStaff } =
    useUniversityStaffPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { universityId } = useParams();
  const { setError: setErrorBanner, clearError: clearErrorBanner } = useErrorBanner();

  const [error, setError] = useState<string | null>(null);
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { rowSelection, setRowSelection, bulkSelectionProps, isSelectedOnly, deselectAll } = useBulkSelection();
  const [selectedStudent, setSelectedStudent] = useState<StudentRow | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const { isCompactLayout } = useIsCompactLayout();

  const { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParamsHook();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams();

  const [expanded, setExpanded] = useState<ExpansionState>({});
  const onExpand = useCallback((rowId: string, column: ExpandedColumn | null) => {
    setExpanded((prev) => ({ ...prev, [rowId]: column }));
  }, []);

  const parsedEnrollmentDateRange = useMemo(() => {
    if (!params.enrollmentDateRange) return undefined;
    if (typeof params.enrollmentDateRange === "string") {
      const [from, to] = params.enrollmentDateRange.split(",");
      return { from, to };
    }
    return params.enrollmentDateRange as { from: string; to: string };
  }, [params.enrollmentDateRange]);

  const initialPatStatus = useMemo(
    () =>
      params.status && params.status.length > 0
        ? mapPATStatusesToGraphQL(Array.isArray(params.status) ? params.status : [params.status])
        : undefined,
    [params.status]
  );

  const initialCohortIds = useMemo(
    () => (Array.isArray(params.cohorts) ? params.cohorts : params.cohorts ? [params.cohorts] : []),
    [params.cohorts]
  );

  const initialOrderBy = useMemo(() => {
    const field = typeof params.sort === "string" ? params.sort : undefined;
    const direction = typeof params.sortDir === "string" ? params.sortDir : undefined;
    if (!field) return undefined;
    return { field, direction: direction === "DESC" ? ("DESC" as const) : ("ASC" as const) };
  }, [params.sort, params.sortDir]);

  const [variables, setVariables] = useState<StudentsQueryVariables>(() => {
    const programsFromUrl = Array.isArray(params.programs) ? params.programs : params.programs ? [params.programs] : [];
    const { curriculumVersions, levels } = parseProgramLevelFilterSelections(programsFromUrl);

    return {
      input: {
        universityLegacyIds: [universityId?.toString() || ""],
        alumni: false,
        search: params.searchTerm || undefined,
        enrollmentDateRange: parsedEnrollmentDateRange,
        patStatus: initialPatStatus as PatStatus[] | undefined,
        curriculumVersions,
        levels,
        cohortIds: initialCohortIds.length > 0 ? initialCohortIds : undefined,
        orderBy: initialOrderBy
      }
    };
  });

  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(
    Array.isArray(params.programs) ? params.programs : params.programs ? [params.programs] : []
  );
  const [selectedPatStatus, setSelectedPatStatus] = useState<PatStatus[]>((params.status as PatStatus[]) || []);
  const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>(
    Array.isArray(params.cohorts) ? params.cohorts : params.cohorts ? [params.cohorts] : []
  );
  const [orderBy, setOrderBy] = useState<ColumnSort[]>(() => {
    const sortField = typeof params.sort === "string" ? params.sort : undefined;
    const sortDir = typeof params.sortDir === "string" ? params.sortDir : undefined;
    if (!sortField) return [];
    return [{ id: sortField, desc: sortDir === "DESC" }];
  });
  const [isGraduationDialogOpen, setIsGraduationDialogOpen] = useState(false);
  const [selectedStudentForGraduation, setSelectedStudentForGraduation] = useState<StudentRow | null>(null);
  const [probationModalState, setProbationModalState] = useState<{
    isOpen: boolean;
    mode: "create" | "end";
    probation: any | null;
    selectedStudent: {
      id: string;
      displayName: string;
    } | null;
    existingActiveProbationCount?: number;
  }>({
    isOpen: false,
    mode: "create",
    probation: null,
    selectedStudent: null
  });

  const [noteDialogState, setNoteDialogState] = useState<{
    isOpen: boolean;
    student: StudentRow | null;
  }>({
    isOpen: false,
    student: null
  });

  const [editCohortDialogState, setEditCohortDialogState] = useState<{
    isOpen: boolean;
    student: StudentRow | null;
  }>({
    isOpen: false,
    student: null
  });

  const { Menu, setMenu } = useTableRowMenu();

  const graduationForm = useForm<{ graduationDate: { startDate: string | null; endDate: string | null } }>({
    defaultValues: { graduationDate: { startDate: null, endDate: null } }
  });

  const filtersForm = useForm<{ enrollmentDateRange: { startDate: Date | null; endDate: Date | null } | null }>({
    defaultValues: {
      enrollmentDateRange: parsedEnrollmentDateRange
        ? { startDate: new Date(parsedEnrollmentDateRange.from), endDate: new Date(parsedEnrollmentDateRange.to) }
        : null
    }
  });

  const { Modal } = useModal();
  const [dateSelectDisabled, setDateSelectDisabled] = useState(true);

  const [createGraduationRequests] = useMutation(CREATE_GRADUATION_REQUESTS_MUTATION, {
    onError: () => {
      setError("Failed to create graduation requests. Please try again.");
    }
  });
  const { id: currentUserId, hasRole } = useCapabilities();
  const canEditCohort = hasRole(ROLE.EDUCATION_ADMIN) || hasRole(ROLE.GLOBAL_ADMIN);

  // Lazy query for cohort enrollment data to get enrollment IDs
  const [getCohortData, { data: cohortData }] = useLazyQuery<GetCohortEnrollmentNotesByUniversityQuery>(
    GET_COHORT_ENROLLMENT_NOTES_BY_UNIVERSITY_QUERY,
    {
      errorPolicy: "all"
    }
  );

  const [getActiveProbationsPage] = useLazyQuery(GET_ACTIVE_UNIVERSITY_PROBATIONS_QUERY, {
    errorPolicy: "all",
    fetchPolicy: "network-only"
  });
  const [activeProbationNodes, setActiveProbationNodes] = useState<
    Array<{
      id: string;
      startedAt: string;
      active: boolean;
      probationType?: string | null;
      person: { id: string };
    }>
  >([]);

  const loadActiveProbations = useCallback(
    async (universityId: string) => {
      const allNodes: Array<{
        id: string;
        startedAt: string;
        active: boolean;
        probationType?: string | null;
        person: { id: string };
      }> = [];
      let hasNextPage = true;
      let after: string | undefined;
      // Backend max page size is 100; page until exhausted (cap guards runaway loops).
      const PAGE_SIZE = 100;
      const MAX_PAGES = 50;
      let pageCount = 0;

      while (hasNextPage && pageCount < MAX_PAGES) {
        pageCount += 1;
        const { data } = await getActiveProbationsPage({
          variables: {
            universityId,
            first: PAGE_SIZE,
            after
          }
        });

        const connection = data?.university?.probations;
        if (!connection?.nodes) {
          break;
        }

        allNodes.push(...connection.nodes);
        hasNextPage = connection.pageInfo?.hasNextPage || false;
        after = connection.pageInfo?.endCursor || undefined;
      }

      setActiveProbationNodes(allNodes);
    },
    [getActiveProbationsPage]
  );

  const [getPATStatusExport] = useLazyQuery<UniversityStudentsQuery>(STUDENTS_QUERY, {
    errorPolicy: "all",
    variables: {
      input: {
        universityLegacyIds: [universityId?.toString() || ""],
        alumni: false
      },
      first: 10000
    }
  });

  useEffect(() => {
    setVariables((prevVariables) => ({
      input: {
        ...prevVariables.input,
        search: debouncedSearch || undefined,
        pgaIds: !isSelectedOnly ? undefined : Object.keys(rowSelection).filter(Boolean)
      }
    }));

    if (debouncedSearch) {
      resetPagination();
    }
  }, [debouncedSearch, isSelectedOnly, rowSelection, resetPagination]);

  const handleSortChange = useCallback(
    (updaterOrValue: any) => {
      const next: ColumnSort[] = typeof updaterOrValue === "function" ? updaterOrValue(orderBy) : updaterOrValue;
      setOrderBy(next);

      const first = next[0];
      if (first) {
        const direction = first.desc ? ("DESC" as const) : ("ASC" as const);
        setVariables((prevVariables: any) => ({
          input: { ...prevVariables.input, orderBy: { field: first.id, direction } }
        }));
        addSearchParam("sort", first.id);
        addSearchParam("sortDir", direction);
      } else {
        setVariables((prevVariables: any) => ({
          input: { ...prevVariables.input, orderBy: undefined }
        }));
        deleteSearchParams("sort");
        deleteSearchParams("sortDir");
      }
      resetPagination();
    },
    [orderBy, addSearchParam, deleteSearchParams, resetPagination]
  );

  useEffect(() => {
    const { curriculumVersions, levels } = parseProgramLevelFilterSelections(selectedPrograms);
    setVariables((prevVariables) => ({
      input: {
        ...prevVariables.input,
        curriculumVersions,
        levels
      }
    }));
  }, [selectedPrograms]);

  useEffect(() => {
    if (isGraduationDialogOpen) {
      setDateSelectDisabled(false);
    } else {
      setDateSelectDisabled(true);
    }
  }, [isGraduationDialogOpen]);

  const { data, loading, refetch } = useQuery(STUDENTS_QUERY, {
    variables: {
      ...variables,
      ...paginationVariables
    }
  });

  // Get university data directly using the legacy ID from URL params
  const { data: universityData } = useQuery(UNIVERSITY_QUERY, {
    variables: {
      legacyId: universityId || ""
    },
    skip: !universityId
  });

  const [selectedStudentsQuery, { data: selectedStudentsData, loading: selectedStudentsLoading }] =
    useLazyQuery(STUDENTS_QUERY);

  // Get university UUID directly from university query
  const universityUuid = universityData?.universities?.[0]?.id;

  const {
    data: pendingRosterData,
    loading: pendingRosterLoading,
    error: pendingRosterError
  } = useQuery(UNIVERSITY_ROSTER_ROWS_EXISTS_QUERY, {
    variables: {
      universityId: universityUuid || "",
      rosterStatus: PENDING_REVIEW_ROSTER_STATUS
    },
    skip: !universityUuid || !isCampusOnlyUniversityStaff,
    fetchPolicy: "network-only"
  });

  const { pendingRosterCheckFailed, blockCampusResubmit, showUploadRosterAction } = campusPendingRosterGate({
    isCampusOnlyUniversityStaff,
    loading: pendingRosterLoading,
    error: pendingRosterError,
    totalCount: pendingRosterData?.university?.universityRosterRows?.totalCount,
    universityReady: Boolean(universityUuid)
  });

  // Trigger cohort + active-probation loads when we have the university UUID
  useEffect(() => {
    if (!universityUuid) return;

    getCohortData({
      variables: {
        universityId: universityUuid
      }
    });
    void loadActiveProbations(universityUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universityUuid]);

  const allStudents = mapStudentsSimplified(
    data?.universityStudents || {
      totalCount: 0,
      pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: undefined, endCursor: undefined },
      nodes: []
    }
  );

  const selectedStudents = mapStudentsSimplified(
    selectedStudentsData?.universityStudents || {
      totalCount: 0,
      pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: undefined, endCursor: undefined },
      nodes: []
    }
  );

  const students = isSelectedOnly ? selectedStudents : allStudents;

  // Create a map of student IDs to their notes count
  const studentNotesMap = useMemo(() => {
    if (!cohortData?.university?.universityCohorts) return new Map();

    const notesMap = new Map();
    cohortData.university.universityCohorts.forEach((cohort) => {
      cohort.enrollments.forEach((enrollment) => {
        const notesCount = enrollment.notes?.length || 0;
        notesMap.set(enrollment.person.id, notesCount);
      });
    });
    return notesMap;
  }, [cohortData]);

  const cohortFilterOptions = useMemo(() => {
    const cohorts = cohortData?.university?.universityCohorts ?? [];
    return [...cohorts]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((cohort) => ({ value: cohort.id, label: cohort.name }));
  }, [cohortData]);

  const studentProbationsMap = useMemo(() => buildStudentProbationsMap(activeProbationNodes), [activeProbationNodes]);

  const handleViewStudent = useCallback((student: StudentRow) => {
    setSelectedStudent(student);
    setIsDetailsPanelOpen(true);
  }, []);

  const handleOpenProbationDialog = useCallback(
    (student: StudentRow) => {
      setProbationModalState({
        isOpen: true,
        mode: "create",
        probation: null,
        selectedStudent: {
          id: student.studentId, // Use studentId (person UUID), not id (pgaId)
          displayName: student.displayName
        },
        existingActiveProbationCount: studentProbationsMap.get(student.studentId)?.count ?? 0
      });
    },
    [studentProbationsMap]
  );

  const handleOpenIndividualGraduationDialog = useCallback(
    (student: StudentRow) => {
      setSelectedStudentForGraduation(student);
      graduationForm.reset({
        graduationDate: { startDate: null, endDate: null }
      });
      setIsGraduationDialogOpen(true);
    },
    [graduationForm]
  );

  const handleOpenNoteDialog = useCallback((student: StudentRow) => {
    setNoteDialogState({ isOpen: true, student });
  }, []);

  const handleCloseNoteDialog = useCallback(() => {
    setNoteDialogState({ isOpen: false, student: null });
  }, []);

  const handleOpenEditCohortDialog = useCallback((student: StudentRow) => {
    setEditCohortDialogState({ isOpen: true, student });
  }, []);

  const handleCloseEditCohortDialog = useCallback(() => {
    setEditCohortDialogState({ isOpen: false, student: null });
  }, []);

  const handleEditCohortSuccess = useCallback(() => {
    refetch();
    getCohortData({ variables: { universityId: universityUuid } });
  }, [refetch, getCohortData, universityUuid]);

  const enrollmentIdForNote = useMemo(
    () =>
      noteDialogState.student ? (getStudentEnrollmentId(noteDialogState.student.studentId, cohortData) ?? null) : null,
    [noteDialogState.student, cohortData]
  );

  const handleNoteSaveSuccess = useCallback(() => {
    getCohortData({ variables: { universityId: universityUuid } });
    handleCloseNoteDialog();
  }, [getCohortData, universityUuid, handleCloseNoteDialog]);

  const columns = useMemo(
    () =>
      buildColumns({
        isCompactLayout,
        hasCheckbox: true,
        navigate,
        location,
        universityId: universityId || "",
        handleViewStudent,
        handleOpenProbationDialog,
        handleOpenIndividualGraduationDialog,
        handleOpenNoteDialog,
        handleOpenEditCohortDialog,
        canEditCohort,
        setMenu,
        actionId,
        studentNotesMap,
        studentProbationsMap,
        expanded,
        onExpand
      }),
    [
      isCompactLayout,
      navigate,
      location,
      universityId,
      handleViewStudent,
      handleOpenProbationDialog,
      handleOpenIndividualGraduationDialog,
      handleOpenNoteDialog,
      handleOpenEditCohortDialog,
      canEditCohort,
      setMenu,
      studentNotesMap,
      studentProbationsMap,
      expanded,
      onExpand
    ]
  );

  const totalSelected = Object.keys(rowSelection).length;

  const filterState = {
    programs: selectedPrograms.length > 0,
    dates: !!(variables.input?.enrollmentDateRange?.from && variables.input?.enrollmentDateRange?.to),
    status: selectedPatStatus.length > 0,
    cohorts: selectedCohortIds.length > 0
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const submitElegibleForGraduation = async (data: { graduationDate: { startDate: string | null } }) => {
    if (!data.graduationDate.startDate) {
      return;
    }

    try {
      let personIds: string[];

      if (selectedStudentForGraduation) {
        personIds = [selectedStudentForGraduation.studentId];
      } else {
        const selectedPgaIds = Object.keys(rowSelection).filter(Boolean);

        personIds = allStudents
          .filter((student) => selectedPgaIds.includes(student.id))
          .map((student) => student.studentId);
      }

      if (personIds.length === 0) {
        setError("Error: No students selected for graduation");
        return;
      }

      if (!universityUuid) {
        setError("Error: University information not found");
        return;
      }

      const { data: mutationData } = await createGraduationRequests({
        variables: {
          input: {
            personIds,
            graduationDate: data.graduationDate.startDate,
            universityId: universityUuid
          }
        }
      });

      if (mutationData?.createGraduationRequests?.success) {
        const count = personIds.length;
        showSnackbar(`${count} ${pluralize(count, "student")} marked eligible for graduation`);
        setIsGraduationDialogOpen(false);
        setSelectedStudentForGraduation(null);
        graduationForm.reset();
        refetch();
      } else {
        setError(
          mutationData?.createGraduationRequests?.message || "Error marking students as eligible for graduation"
        );
      }
    } catch (error) {
      setError("Error marking students as eligible for graduation");
    }
  };

  const handleOpenGraduationDialog = () => {
    setSelectedStudentForGraduation(null);
    graduationForm.reset({ graduationDate: { startDate: null, endDate: null } });
    setIsGraduationDialogOpen(true);
  };

  const handleCloseGraduationDialog = () => {
    setIsGraduationDialogOpen(false);
    setSelectedStudentForGraduation(null);
    graduationForm.reset({ graduationDate: { startDate: null, endDate: null } });
    setError(null);
  };

  const handleClearAllFilters = () => {
    setSelectedPrograms([]);
    setSelectedPatStatus([]);
    setSelectedCohortIds([]);
    filtersForm.reset({ enrollmentDateRange: null });
    setVariables((prevVariables: any) => ({
      input: {
        ...prevVariables.input,
        curriculumVersions: undefined,
        levels: undefined,
        patStatus: undefined,
        enrollmentDateRange: undefined,
        cohortIds: undefined
      }
    }));
    deleteSearchParams("programs[]");
    deleteSearchParams("status[]");
    deleteSearchParams("enrollmentDateRange");
    deleteSearchParams("cohorts[]");
    resetPagination();
  };

  useEffect(() => {
    if (isSelectedOnly) {
      const selectedPgaIds = Object.keys(rowSelection).filter(Boolean);

      if (selectedPgaIds.length > 0) {
        selectedStudentsQuery({
          variables: {
            input: {
              universityLegacyIds: [universityId?.toString() || ""],
              alumni: false,
              pgaIds: selectedPgaIds
            }
          }
        });
      }
    }
  }, [isSelectedOnly, rowSelection, selectedStudentsQuery, universityId]);

  const graduationCount = selectedStudentForGraduation ? 1 : totalSelected;

  const hasFiltersApplied =
    selectedPrograms.length > 0 ||
    selectedPatStatus.length > 0 ||
    selectedCohortIds.length > 0 ||
    !!variables.input?.enrollmentDateRange ||
    !!searchValue;

  const [isGeneratingPDFs, setIsGeneratingPDFs] = useState(false);

  const handleExportPATStatuses = useCallback(async () => {
    try {
      setIsGeneratingPDFs(true);
      clearErrorBanner();

      const allNodes: any[] = [];
      let hasNextPage = true;
      let after: string | undefined = undefined;
      const baseVariables = {
        input: {
          universityLegacyIds: [universityId?.toString() || ""],
          alumni: false
        }
      };

      const MAX_PAGES = 25;
      let pageCount = 0;

      while (hasNextPage && pageCount < MAX_PAGES) {
        pageCount++;
        const { data, error: queryError } = await getPATStatusExport({
          variables: {
            ...baseVariables,
            after
          }
        });

        if (queryError) {
          setErrorBanner(`Error fetching data: ${queryError.message}`);
          return;
        }

        if (!data?.universityStudents?.nodes) {
          break;
        }

        allNodes.push(...data.universityStudents.nodes);
        hasNextPage = data.universityStudents.pageInfo?.hasNextPage || false;
        after = data.universityStudents.pageInfo?.endCursor || undefined;
      }

      if (pageCount >= MAX_PAGES && hasNextPage) {
        setErrorBanner(
          `Reached maximum page limit (${MAX_PAGES}). Only ${allNodes.length} students were exported. Please contact support if you need to export more.`
        );
      }

      if (allNodes.length === 0) {
        setErrorBanner("No student data available");
        return;
      }

      const students: PATStatusExportRow[] = allNodes.map((node, index) => {
        try {
          const patStatus = node.prospectProgram?.playingAbilityTestStatus;

          if (!node.prospectProgram || !patStatus) {
            return {
              id: node.id,
              pgaId: node.pgaId,
              displayName: node.displayName,
              email: node.email || "",
              patStatus: "NOT STARTED",
              patStatusDate: null,
              patStartDate: null,
              patExpirationDate: null,
              completed: false,
              submissions: [],
              activeSuspension: null,
              patDate: null,
              patScore: null
            };
          }

          const statusResult = buildPATStatusForNonMembers(patStatus as any);
          // Use raw status field first, fallback to buildPATStatusForNonMembers result
          let rawStatus = (patStatus.status || "").toUpperCase();
          if (rawStatus === "QUALIFIED") {
            rawStatus = "QUALIFYING";
          }
          const patStatusValue = rawStatus || statusResult.value.toUpperCase();

          const submissions = patStatus.submissions || [];
          const sortedSubmissions = submissions
            .filter((submission: { date?: string | null }) => submission.date)
            .sort(
              (a: { date: string }, b: { date: string }) => new Date(b.date).getTime() - new Date(a.date).getTime()
            );
          const mostRecentSubmission = sortedSubmissions[0] || null;

          // Determine patDate and patScore based on status
          let patDate: string | null = null;
          let patScore: number | null = null;

          if (patStatusValue === "PASSED") {
            // For PASSED: show most recent passed date and score
            const passedSubmissions = sortedSubmissions.filter(
              (sub: { result?: string | null }) => sub.result?.toUpperCase() === "PASSED"
            );
            const mostRecentPassedSubmission = passedSubmissions[0] || null;

            if (mostRecentPassedSubmission) {
              patDate = mostRecentPassedSubmission.date || null;
              // Extract score - prefer totalScore, fallback to round scores if available
              if (
                mostRecentPassedSubmission.totalScore !== null &&
                mostRecentPassedSubmission.totalScore !== undefined
              ) {
                patScore = mostRecentPassedSubmission.totalScore;
              } else if (
                mostRecentPassedSubmission.roundOneScore !== null &&
                mostRecentPassedSubmission.roundTwoScore !== null
              ) {
                patScore =
                  (mostRecentPassedSubmission.roundOneScore || 0) + (mostRecentPassedSubmission.roundTwoScore || 0);
              } else if (mostRecentPassedSubmission.roundOneScore !== null) {
                patScore = mostRecentPassedSubmission.roundOneScore;
              }
            }
          } else if (patStatusValue === "FAILED") {
            // For FAILED: show most recent failed date and score
            const failedSubmissions = sortedSubmissions.filter((sub: { result?: string | null }) => {
              const resultUpper = sub.result?.toUpperCase();
              return resultUpper === "FAILED";
            });
            const mostRecentFailedSubmission = failedSubmissions[0] || null;

            if (mostRecentFailedSubmission) {
              patDate = mostRecentFailedSubmission.date || null;
              // Extract score - prefer totalScore (if > 0), fallback to round scores if available
              const totalScore = mostRecentFailedSubmission.totalScore;
              if (totalScore !== null && totalScore !== undefined && totalScore > 0) {
                patScore = totalScore;
              } else if (
                mostRecentFailedSubmission.roundOneScore !== null &&
                mostRecentFailedSubmission.roundTwoScore !== null
              ) {
                patScore =
                  (mostRecentFailedSubmission.roundOneScore || 0) + (mostRecentFailedSubmission.roundTwoScore || 0);
              } else if (mostRecentFailedSubmission.roundOneScore !== null) {
                patScore = mostRecentFailedSubmission.roundOneScore;
              } else if (mostRecentFailedSubmission.roundTwoScore !== null) {
                patScore = mostRecentFailedSubmission.roundTwoScore;
              }
            }
          } else if (patStatusValue === "QUALIFYING") {
            // For QUALIFYING: show qualifying date only (no score)
            const qualifyingSubmissions = sortedSubmissions.filter(
              (sub: { result?: string | null }) =>
                sub.result?.toUpperCase() === "QUALIFIED" || sub.result?.toUpperCase() === "QUALIFYING"
            );
            const mostRecentQualifyingSubmission = qualifyingSubmissions[0] || null;

            if (mostRecentQualifyingSubmission) {
              patDate = mostRecentQualifyingSubmission.date || null;
              // Don't show score for qualifying status
              patScore = null;
            }
          }

          return {
            id: node.id,
            pgaId: node.pgaId,
            displayName: node.displayName,
            email: node.email || "",
            patStatus: patStatusValue,
            patStatusDate: mostRecentSubmission?.date || null,
            patStartDate: patStatus?.startDate || null,
            patExpirationDate: patStatus?.expirationDate || null,
            completed: patStatus?.completed || false,
            submissions: submissions.map(
              (sub: {
                patType?: string | null;
                date?: string | null;
                result?: string | null;
                roundOneScore?: number | null;
                roundTwoScore?: number | null;
                totalScore?: number | null;
                targetScore?: number | null;
                roundOneId?: string | null;
                roundTwoId?: string | null;
                additionalStatus?: string | null;
                note?: string | null;
                slug?: string | null;
                name?: string | null;
                suspension?: { startDate: string; endDate: string } | null;
                id?: string | null;
              }) => ({
                id: sub.id || null,
                patType: sub.patType || "StandardPat",
                date: sub.date,
                roundOneScore: sub.roundOneScore ?? null,
                roundTwoScore: sub.roundTwoScore ?? null,
                roundOneId: sub.roundOneId || null,
                roundTwoId: sub.roundTwoId || null,
                totalScore: sub.totalScore ?? null,
                targetScore: sub.targetScore ?? null,
                result: sub.result,
                additionalStatus: sub.additionalStatus || null,
                note: sub.note || null,
                slug: sub.slug || null,
                name: sub.name || null,
                suspension: sub.suspension || null
              })
            ),
            activeSuspension: patStatus?.activeSuspension || null,
            patDate,
            patScore
          };
        } catch (nodeError) {
          return {
            id: node.id || `unknown-${index}`,
            pgaId: node.pgaId || "N/A",
            displayName: node.displayName || "Unknown Student",
            email: node.email || "",
            patStatus: "NOT STARTED",
            patStatusDate: null,
            patStartDate: null,
            patExpirationDate: null,
            completed: false,
            submissions: [],
            activeSuspension: null,
            patDate: null,
            patScore: null
          };
        }
      });

      const universityName = allNodes[0]?.university?.name || universityData?.universities?.[0]?.name;

      generateAllStudentsPATStatusPDF(students, universityName);
      showSnackbar(
        `Successfully generated PAT status report with ${students.length} student${students.length !== 1 ? "s" : ""}`
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error generating PDFs";
      setErrorBanner(`Error generating PDFs: ${errorMessage}`);
    } finally {
      setIsGeneratingPDFs(false);
    }
  }, [getPATStatusExport, setErrorBanner, clearErrorBanner, universityData?.universities, universityId]);

  return (
    <>
      {pendingRosterCheckFailed ? (
        <CampusPendingRosterCheckFailedBanner className="mt-8" />
      ) : blockCampusResubmit ? (
        <CampusPendingRosterBanner className="mt-8" />
      ) : null}
      <TableHeading
        heading="PGM Program Roster"
        body="Search for or view all current students associated with this PGM University Program"
        topSpacing={blockCampusResubmit || pendingRosterCheckFailed ? "none" : "default"}
        actions={[
          ...(showUploadRosterAction
            ? [
                {
                  label: "Upload Roster",
                  icon: "upload" as MaterialNames,
                  variant: "outlined" as const,
                  onClick: () => navigate(`/education/universities/${universityId}/students/upload-roster`)
                }
              ]
            : []),
          ...(canAccessPeoplePages
            ? [
                {
                  label: "Export PAT Statuses",
                  icon: "download" as MaterialNames,
                  variant: "outlined" as const,
                  onClick: handleExportPATStatuses,
                  testId: "export-pat-statuses-button",
                  render: () => (
                    <Button
                      variant="text"
                      icon="download"
                      onClick={handleExportPATStatuses}
                      disabled={isGeneratingPDFs}
                      testId="export-pat-statuses-button"
                    >
                      {isGeneratingPDFs ? "Generating PDF..." : "Export PAT Statuses"}
                    </Button>
                  )
                }
              ]
            : [])
        ]}
      />
      <Table
        data={students}
        loading={loading || selectedStudentsLoading}
        columns={columns}
        enableRowSelection={true}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        sortingState={orderBy}
        onSortingChange={handleSortChange}
        serverSideSorting={true}
        onClickRow={(row) => {
          const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
          const separator = searchParamsUrl ? "&" : "";
          navigate(
            `/education/universities/${universityId}/students/${row.original.pgaId}?${searchParamsUrl}${separator}returnUrl=${returnUrl}`
          );
        }}
        data-testid="students-table"
        renderExpandedRow={({ row }) => {
          if (isCompactLayout) {
            return <ExpandedProgramLevelsRow row={row} />;
          }

          const rowExpansion = expanded[row.id];

          if (rowExpansion === "notes") {
            const notes = getStudentNotes(row.original.studentId, cohortData);
            const enrollmentId = getStudentEnrollmentId(row.original.studentId, cohortData);
            return (
              <NotesTable
                variant="details-panel"
                data={notes}
                title={`Notes (${notes.length})`}
                noteEditing={
                  enrollmentId
                    ? {
                        enabled: true,
                        notableId: enrollmentId,
                        notableType: NoteNotableValues.UNIVERSITY_COHORT_ENROLLMENT,
                        refetchQueries: [GET_COHORT_ENROLLMENT_NOTES_BY_UNIVERSITY_QUERY],
                        pgaId: row.original.pgaId
                      }
                    : undefined
                }
              />
            );
          }

          return <ExpandedProgramLevelsRow row={row} />;
        }}
        hasActiveFilters={hasFiltersApplied}
        renderControls={(table: TableComponent<StudentRow>, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(data?.universityStudents)}
            onSortClick={onSortClick}
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            onClearAllFilters={handleClearAllFilters}
            activeFilters={activeFilterCount}
            bulkActions={[
              {
                label: "Mark as Eligible for Graduation",
                onClick: handleOpenGraduationDialog,
                testId: "graduation-button"
              }
            ]}
            {...bulkSelectionProps}
            onDeselectAll={deselectAll}
            totalRowCount={totalSelected}
            onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
            allRowsSelected={table.getIsAllRowsSelected()}
            someRowsSelected={table.getIsSomeRowsSelected()}
          >
            <SearchInput
              placeholder="Search Students"
              query={searchValue}
              onClear={clearSearch}
              onChange={setSearchValue}
            />
            <div data-name="programs">
              <MultiSelect
                label="Program / Level"
                options={FILTER_PROGRAM_LEVEL_OPTIONS}
                selectedValues={selectedPrograms}
                onSelect={(value, checked) => {
                  const updatedPrograms = checked
                    ? [...selectedPrograms, value]
                    : selectedPrograms.filter((v) => v !== value);

                  setSelectedPrograms(updatedPrograms);

                  const { curriculumVersions, levels } = parseProgramLevelFilterSelections(updatedPrograms);

                  setVariables((prevVariables: any) => ({
                    input: {
                      ...prevVariables.input,
                      curriculumVersions,
                      levels
                    }
                  }));

                  addSearchParam("programs[]", updatedPrograms.join(","));
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedPrograms([]);
                  setVariables((prevVariables: any) => ({
                    input: {
                      ...prevVariables.input,
                      curriculumVersions: undefined,
                      levels: undefined
                    }
                  }));
                  deleteSearchParams("programs[]");
                  resetPagination();
                }}
              />
            </div>
            <div data-name="dates" data-filter-size="custom">
              <Controller
                name="enrollmentDateRange"
                control={filtersForm.control}
                render={({ field }) => (
                  <DateSelect
                    key={field.value ? `date-${field.value.startDate}-${field.value.endDate}` : "date-empty"}
                    label="Enrollment Date Range"
                    placeholder="Show All"
                    useModal={true}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      if (typeof e === "object" && e !== null && e.startDate && e.endDate) {
                        const startDate =
                          e.startDate instanceof Date
                            ? e.startDate.toISOString().split("T")[0]
                            : e.startDate.toString();
                        const endDate =
                          e.endDate instanceof Date ? e.endDate.toISOString().split("T")[0] : e.endDate.toString();

                        setVariables((prevVariables: any) => ({
                          ...prevVariables,
                          input: {
                            ...prevVariables.input,
                            enrollmentDateRange: { from: startDate, to: endDate }
                          }
                        }));
                        addSearchParam("enrollmentDateRange", [e.startDate, e.endDate].join(","));
                        resetPagination();
                      } else if (
                        e === null ||
                        (typeof e === "object" && e !== null && e.startDate === null && e.endDate === null)
                      ) {
                        setVariables((prevVariables: any) => ({
                          ...prevVariables,
                          input: {
                            ...prevVariables.input,
                            enrollmentDateRange: undefined
                          }
                        }));
                        deleteSearchParams("enrollmentDateRange");
                        resetPagination();
                      }
                    }}
                  />
                )}
              />
            </div>
            <div data-name="cohorts">
              <MultiSelect
                label="Cohort"
                options={cohortFilterOptions}
                selectedValues={selectedCohortIds}
                onSelect={(value, checked) => {
                  const updated = checked
                    ? [...selectedCohortIds, value]
                    : selectedCohortIds.filter((v) => v !== value);

                  setSelectedCohortIds(updated);

                  setVariables((prevVariables: any) => ({
                    input: {
                      ...prevVariables.input,
                      cohortIds: updated.length > 0 ? updated : undefined
                    }
                  }));

                  addSearchParam("cohorts[]", updated.join(","));
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedCohortIds([]);
                  setVariables((prevVariables: any) => ({
                    input: { ...prevVariables.input, cohortIds: undefined }
                  }));
                  deleteSearchParams("cohorts[]");
                  resetPagination();
                }}
              />
            </div>
            <div data-name="status">
              <MultiSelect
                label="PAT Status"
                options={PAT_STATUS_OPTIONS}
                selectedValues={selectedPatStatus}
                onSelect={(value, checked) => {
                  const updatedStatus = checked
                    ? [...selectedPatStatus, value as PatStatus]
                    : selectedPatStatus.filter((v) => v !== value);

                  setSelectedPatStatus(updatedStatus);

                  setVariables((prevVariables: any) => ({
                    input: {
                      ...prevVariables.input,
                      patStatus: mapPATStatusesToGraphQL(updatedStatus)
                    }
                  }));

                  addSearchParam("status[]", updatedStatus.join(","));
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedPatStatus([]);

                  setVariables((prevVariables: any) => ({
                    input: {
                      ...prevVariables.input,
                      patStatus: undefined
                    }
                  }));

                  deleteSearchParams("status[]");
                  resetPagination();
                }}
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter pagination={createFooterPagination(data?.universityStudents)} {...bulkSelectionProps} />
        )}
      />

      <Menu />

      {probationModalState.isOpen && (
        <ProbationModal
          isOpen={probationModalState.isOpen}
          universityLegacyId={universityData?.universities?.[0]?.legacyId || universityId || ""}
          mode={probationModalState.mode}
          probation={probationModalState.probation}
          selectedStudent={probationModalState.selectedStudent}
          existingActiveProbationCount={probationModalState.existingActiveProbationCount ?? 0}
          onClose={() => {
            setProbationModalState({
              isOpen: false,
              mode: "create",
              probation: null,
              selectedStudent: null,
              existingActiveProbationCount: 0
            });
          }}
          onSuccess={() => {
            refetch();
            if (universityUuid) {
              void loadActiveProbations(universityUuid);
            }
          }}
        />
      )}

      {noteDialogState.isOpen && enrollmentIdForNote && (
        <AddEditNoteSideSheet
          open={noteDialogState.isOpen}
          onClose={handleCloseNoteDialog}
          notableId={enrollmentIdForNote}
          notableType={NoteNotableValues.UNIVERSITY_COHORT_ENROLLMENT}
          refetchQueries={[GET_COHORT_ENROLLMENT_NOTES_BY_UNIVERSITY_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={noteDialogState.student?.pgaId}
          objectType="student"
          onSaveSuccess={handleNoteSaveSuccess}
        />
      )}

      {editCohortDialogState.isOpen &&
        editCohortDialogState.student &&
        (() => {
          const studentId = editCohortDialogState.student.studentId;
          const enrollmentId = getStudentEnrollmentId(studentId, cohortData);
          const availableCohorts =
            cohortData?.university?.universityCohorts?.map((c) => ({ id: c.id, name: c.name })) ?? [];

          return enrollmentId ? (
            <EditCohortDialog
              isOpen={editCohortDialogState.isOpen}
              onClose={handleCloseEditCohortDialog}
              onSuccess={handleEditCohortSuccess}
              enrollmentId={enrollmentId}
              selectedStudent={{
                displayName: editCohortDialogState.student.displayName,
                currentCohort: editCohortDialogState.student.universityCohort ?? null
              }}
              cohorts={availableCohorts}
            />
          ) : null;
        })()}

      {isGraduationDialogOpen && (
        <Modal
          onClosed={handleCloseGraduationDialog}
          hasFormElements
          displayOverflow
          headline="Mark as Eligible for Graduation"
          action={
            <div className="flex gap-2">
              <Button
                testId="graduation-button-submit"
                variant="filled"
                disabled={!graduationForm.watch("graduationDate")?.startDate}
                onClick={graduationForm.handleSubmit(submitElegibleForGraduation)}
              >
                Graduate {graduationCount} Student{graduationCount === 1 ? "" : "s"}
              </Button>
            </div>
          }
        >
          <FormProvider {...graduationForm}>
            <div>
              {error && (
                <div className="mb-4">
                  <Banner
                    variant="error"
                    description={error}
                    inline={true}
                    closable={true}
                    onClose={() => setError(null)}
                  />
                </div>
              )}
              <div className="font-label-large font-medium text-sys-brand-on-surface mb-2">
                Total Students to Mark as Eligible for Graduation: {graduationCount}
              </div>
              <div className="mb-6 text-body-medium text-sys-brand-on-surface">
                Are you sure you want to mark the selected student{graduationCount === 1 ? "" : "s"} as eligible for
                graduation? Please ensure that the student{graduationCount === 1 ? " has" : "s have"} met all program
                requirements. PGA National will be notified to review students and complete the graduation process, at
                which time the students will be visible on the Alumni page.
              </div>
              <div className="mb-4">
                <Controller
                  name="graduationDate"
                  control={graduationForm.control}
                  rules={{ required: "Graduation date is required" }}
                  render={({ field, fieldState }) => (
                    <DateSelect
                      label="Graduation Date *"
                      placeholder="Select Date"
                      useModal={true}
                      value={field.value}
                      onChange={field.onChange}
                      useSingleDate
                      showFooter={false}
                      error={fieldState.invalid}
                      errorMessage={fieldState.error?.message}
                      disabled={dateSelectDisabled}
                    />
                  )}
                />
              </div>
            </div>
          </FormProvider>
        </Modal>
      )}

      <PersonSideSheet
        open={isDetailsPanelOpen && !!selectedStudent}
        onClose={() => setIsDetailsPanelOpen(false)}
        pgaId={selectedStudent?.pgaId ?? null}
        title="Student Details"
        showPersonalDetails={true}
        universityName={selectedStudent?.university?.name}
        showProfileActions={shouldShowPersonProfileActions}
        customActionButtons={
          selectedStudent
            ? [
                {
                  text: "View PGM Details",
                  variant: "outlined" as const,
                  onClick: () => {
                    const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
                    const separator = searchParamsUrl ? "&" : "";
                    navigate(
                      `/education/universities/${universityId}/students/${selectedStudent.pgaId}?${searchParamsUrl}${separator}returnUrl=${returnUrl}`
                    );
                  }
                }
              ]
            : undefined
        }
      />
    </>
  );
}
